import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { makeStyles } from '@material-ui/core/styles';
import InnerWindow from '../../../components/InnerWindow';
import clsx from 'clsx';
import ParametersTable from './ParametersTable';
import ParametersGraph from './ParametersGraph';
import { CircularProgress, IconButton } from '@material-ui/core';
import { hexColorToCSSFilter } from '../../../modules/CSSUtils';
import downloadIcon from '../../../res/images/download.svg';
import Authentication from '../../../modules/Authentication';
import tableIcon from '../../../res/images/view_table.svg';
import graphIcon from '../../../res/images/view_chart.svg';
import capsulesIcon from '../../../res/images/view_capsules.svg';
import Dialog from '@material-ui/core/Dialog';
import Button from '../../../components/Button';
import ParametersCapsules from './ParametersCapsules';

const TEMPERATURE_VALUE_NOT_AVAILABLE = 204.8; // When the temperature value is this, this means no sensor is attached

const gApiClient = Authentication.getAPIClient();

const useStyles = makeStyles({
    rightPart: {
        marginLeft: 'auto',
        paddingRight: '10px',
    },
    window: {
        backgroundColor: '#f0f3f9',
    },
    iconButton: {
        width: '30px',
        height: '30px',
    },
    iconButtonRight: {
        width: '60px',
        height: '60px',
    },
    rightIcon: {
        padding: 0,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        height: 0,
        flexGrow: 1,
    },
    openMachineIcon: {
        padding: '8px',
        filter: hexColorToCSSFilter('#818FA8'),
    },
    dialog: {
        margin: '0px !important',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        padding: '0px',
        maxWidth: 'unset',
    },
    dialogMessage: {
        fontSize: '20px',
        textAlign: 'center',
        padding: '20px',
    },
    dialogHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#8090a8',
        paddingTop: '10px',
        paddingBottom: '10px',
    },
    dialogTitle: {
        textTransform: 'uppercase',
        color: '#ffffff',
        fontSize: '18px',
        flexGrow: 1,
        textAlign: 'center',
        paddingLeft: '20px',
        paddingRight: '20px',
    },
    dialogClose: {},
    dialogCloseIcon: {
        filter: hexColorToCSSFilter('#ffffff'),
    },
    dialogButtonRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: '10px',
        justifyContent: 'center',
        gap: '20px',
    },
    dialogConfirm: {
        marginTop: '15px',
        width: '150px',
        alignSelf: 'center',
        marginBottom: '10px',
    },
    dialogContent: {
        background: '#FFFFFF',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
    },
    dialogRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: '10px',
    },
});

/** Whether or not a temperature value means it's not really available */
export function temperatureIsUnavailable(temperature) {
    return !temperature || temperature === TEMPERATURE_VALUE_NOT_AVAILABLE;
}

/** Formats a temperature value */
export function formatTemperature(temperature) {
    return temperatureIsUnavailable(temperature)
        ? 'N/A'
        : temperature.toFixed(2);
}

const VIEW_TYPE_TABLE = 'table';
const VIEW_TYPE_GRAPH = 'graph';
const VIEW_TYPE_CAPSULES = 'capsules';

/** Single machine view (machine details, params, graph, etc.) */
export default function MachineParameters(props) {
    const classes = useStyles();
    const machine = props.machine;
    const [viewType, setViewType] = useState(VIEW_TYPE_TABLE);
    const [latestTimestamp, setLatestTimestamp] = useState(
        machine && machine.latest_timestamp
            ? DateTime.fromMillis(machine.latest_timestamp).toUTC()
            : null
    );
    const [isDownloadingLogs, setIsDownloadingLogs] = useState(false);
    const [downloadingLogsMessage, setDownloadingLogsMessage] = useState(null);

    const onNewLogs = (newLogs) => {
        props.onNewLogs(newLogs);

        if (!newLogs || newLogs.length === 0) return;
        const latestLog = newLogs[newLogs.length - 1];
        setLatestTimestamp(
            DateTime.fromMillis(latestLog.timestamp)
                .toUTC()
                .plus({ minutes: latestLog.timezoneOffset })
        );
    };

    const downloadLogs = async () => {
        const machineId = machine._id;
        console.log('MACHINE ID', machineId);
        setIsDownloadingLogs(true);

        const startDate = DateTime.local().startOf('day');
        const endDate = startDate.plus({ days: 1 });

        try {
            await gApiClient.callApi(
                'admin/generateCSVFile',
                'POST',
                {},
                {
                    machine_ids: machineId,
                    // Also account for local timezone
                    start_date: startDate
                        .minus({ minutes: new Date().getTimezoneOffset() })
                        .toMillis(),
                    end_date: endDate
                        .minus({ minutes: new Date().getTimezoneOffset() })
                        .toMillis(),
                    minute_offset: -new Date().getTimezoneOffset(),
                    raw_logs_only: 0,
                    summary_report: 0,
                    reduced_report: 1,
                    csv_only: 0,
                }
            );

            setDownloadingLogsMessage(
                `Logs for ${machine.alias} for today will be sent to your email`
            );
        } catch (e) {
            console.error(e);
            const message = e.response?.data?.Message || e.message;
            setDownloadingLogsMessage(message);
        }

        setIsDownloadingLogs(false);
    };

    return (
        <InnerWindow
            className={clsx(classes.window, props.className)}
            title={
                machine
                    ? `${machine.alias} / Parameters (last updated: ${
                          latestTimestamp
                              ? latestTimestamp.toFormat('dd/MM/yyyy HH:mm')
                              : 'N/A'
                      })`
                    : ''
            }
            leftView={
                isDownloadingLogs ? (
                    <CircularProgress
                        size={48}
                        thickness={3}
                        className={classes.openMachineIcon}
                    />
                ) : (
                    <IconButton
                        onClick={() => {
                            downloadLogs();
                        }}
                        className={classes.openMachineIcon}
                    >
                        <img src={downloadIcon} alt="Download Logs" />
                    </IconButton>
                )
            }
            rightView={
                <div className={classes.rightPart}>
                    <IconButton
                        onClick={() => setViewType(VIEW_TYPE_TABLE)}
                        classes={{
                            root: classes.rightIcon,
                        }}
                    >
                        <img
                            src={tableIcon}
                            alt={'Table'}
                            className={classes.iconButtonRight}
                        />
                    </IconButton>
                    <IconButton
                        onClick={() => setViewType(VIEW_TYPE_GRAPH)}
                        classes={{
                            root: classes.rightIcon,
                        }}
                    >
                        <img
                            src={graphIcon}
                            alt={'Graph'}
                            className={classes.iconButtonRight}
                        />
                    </IconButton>
                    <IconButton
                        onClick={() => setViewType(VIEW_TYPE_CAPSULES)}
                        classes={{
                            root: classes.rightIcon,
                        }}
                    >
                        <img
                            src={capsulesIcon}
                            alt={'Capsules'}
                            className={classes.iconButtonRight}
                        />
                    </IconButton>
                </div>
            }
        >
            <div className={classes.content}>
                {viewType === VIEW_TYPE_TABLE && (
                    <ParametersTable
                        machineId={props.machineId}
                        onNewLogs={onNewLogs}
                        machine={machine}
                    />
                )}
                {viewType === VIEW_TYPE_GRAPH && (
                    <ParametersGraph
                        machineId={props.machineId}
                        onNewLogs={onNewLogs}
                        machine={machine}
                    />
                )}
                {viewType === VIEW_TYPE_CAPSULES && (
                    <ParametersCapsules
                        machineId={props.machineId}
                        machine={machine}
                    />
                )}
            </div>

            <Dialog
                onClose={() => setDownloadingLogsMessage(null)}
                open={Boolean(downloadingLogsMessage)}
                style={{ position: 'absolute' }}
                BackdropProps={{ style: { position: 'absolute' } }}
                classes={{ paper: classes.dialog }}
            >
                <div className={classes.dialogHeader}>
                    <div className={classes.dialogTitle}>Download Logs</div>
                </div>
                <div className={classes.dialogMessage}>
                    {downloadingLogsMessage}
                </div>
                <Button
                    className={classes.dialogConfirm}
                    label="Close"
                    onClick={() => setDownloadingLogsMessage(null)}
                />
            </Dialog>
        </InnerWindow>
    );
}
